import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import { getPriorityColor, getStatusColor } from "../../utils/colors";

const formatText = (text = "") =>
  text
    .toLowerCase()
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString();
};

export default function WorkflowView() {
  const navigate = useNavigate();
  const location = useLocation();

  const viewTask = location.state?.workflow;

  useEffect(() => {
    if (!viewTask) navigate(-1);
  }, [viewTask]);

  if (!viewTask) return null;

  return (
    <div
      onClick={() => navigate(-1)}
      // className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center border-1 border-red-500"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-2xl max-w-[100vw] w-full min-h-[90vh] overflow-x-auto"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-2 py-2 border-b">
          <div className="text-lg font-semibold text-gray-900">
            {/* <span><ArrowLeft size={18}/></span> */}
            Workflow Details
          </div>

          <button
            onClick={() => navigate(-1)}
            className="p-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-2 text-sm space-y-1">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {viewTask?.name}
            </div>
            <div className="text-xs text-gray-400">Workflow Overview</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-1">
            <InfoCard
              label="Assignee"
              value={viewTask?.assignedToName}
            />

            <InfoCard
              label="Workflow"
              value={formatText(viewTask?.template)}
            />

            <InfoCard
              label="Start Date"
              value={viewTask?.startDate}
            />

            <InfoCard
              label="Due Date"
              value={viewTask?.dueDate}
            />

            <StatusBadge status={viewTask?.status} />
          </div>

          {/* Description */}
          {viewTask?.description && (
            <div className="border-t pt-1">
              <p className="text-xs text-gray-400 mb-1">Description</p>
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-gray-700">
                {viewTask.description}
              </div>
            </div>
          )}
        </div>

        {/* Tasks */}
        <div className="p-2 grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {viewTask?.tasks?.length > 0 ? (
            viewTask.tasks.map(task => (
              <div
                key={task.id}
                className="border border-gray-200 rounded-lg p-3 shadow-sm"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-grow">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-base">
                        {task.title}
                      </div>

                      <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>

                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(task.status)}`}>
                        {formatText(task.status)}
                      </span>
                    </div>

                    {task.description && (
                      <p className="text-gray-600 text-sm mt-1">
                        {task.description}
                      </p>
                    )}

                    <div className="flex gap-4 text-xs text-gray-500 mt-2">
                      <span>Start: {formatDate(task.startDate)}</span>
                      <span>Due: {formatDate(task.dueDate)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-4">
              No tasks available.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* SAME COMPONENTS */

const InfoCard = ({ label, value }) => (
  <div className="bg-gray-50 rounded p-2">
    <div className="text-xs text-gray-400">{label}</div>
    <div className="font-medium">{value || "N/A"}</div>
  </div>
);

const StatusBadge = ({ status }) => (
  <div className="bg-gray-50 rounded p-2">
    <div className="text-xs text-gray-400">Status</div>
    <span className="px-3 py-1 rounded-full text-xs bg-blue-100">
      {formatText(status)}
    </span>
  </div>
);
